<template>
  <div class="main-toast-wrapper display-flex align-items-center justify-between gap-16">
    <div class="display-flex gap-12">
      <div :class="['toast-status display-flex justify-center align-items-center animated-border', typeClass]">
        <IconTypes :name="iconComponent"
                   :size="24"
        />
      </div>

      <div class="content-toast display-flex flex-column align-items-start justify-center">
        <p class="compact-500-accent">
          {{ textTitle }}
        </p>
        <p class="text-400-default text-color-secondary">
          {{ description }}
        </p>
      </div>
    </div>
    <button aria-label="Close toast"
            class="toast-close-button cursor-pointer display-flex align-items-center justify-center"
            type="button"
            @click.stop="closeToast"
    >
      <IconTypes name="icon/close"
                 :size="20"
                 :style="closeIconStyle"
      />
    </button>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';

import { useColorMode } from '#imports';

import type { PropsToast, TypeToast } from './Toast.types';

const props = defineProps<PropsToast>();
const repPath = ref<SVGPathElement | null>(null);
const pathLength = ref<number>(212.24021911621094);

const typeClassMapping: Record<TypeToast, string> = {
    'type.info': 'async-toast__neutral',
    'type.error': 'async-toast__error',
    'type.success': 'async-toast__success'
};

const iconMapping: Record<TypeToast, SvgNameKeysSlash> = {
    'type.info': 'icon/hourglass',
    'type.error': 'icon/close',
    'type.success': 'icon/check'
};

const emit = defineEmits(['close-toast']);

function closeToast() {
    emit('close-toast');
}

const timer = computed(() => props.timer ?? 3000);
const typeClass = computed(() => typeClassMapping[props.type]);

const iconComponent = computed(() => iconMapping[props.type]);

const colorMode = useColorMode();

const closeIconStyle = computed(() => colorMode.preference === 'dark-mode' ? { color: 'var(--layer-floor-2)' } : { color: 'var(--content-primary-inverted)' });

onMounted(() => {
    if (repPath.value) {
        pathLength.value = repPath.value.getTotalLength();
    }
});
</script>

<style lang="scss">

@keyframes border-animation {
  0% {
    border: 2px solid transparent;
  }

  50% {
    border: 2px solid currentcolor;
  }

  100% {
    border: 2px solid transparent;
  }
}

.toast-status.animated-border {
  width: 36px;
  height: 36px;
  animation: border-animation 3s infinite;
  border-radius: 4px;
}

.border-container {
  position: relative;
  margin-right: 4px;
}

.content-toast {
  p::first-letter {
    text-transform: capitalize;
  }
}

.toast-img-w {
  display: inline;
  position: absolute;
  width: 56px;
  height: 56px;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: 24px 24px;
}

.main-toast-wrapper {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 10px 20px;
  border-radius: 4px;
  background-color: var(--layer-floor-2);
  pointer-events: auto;
  user-select: auto;
}

.toast-status {
  width: 36px;
  height: 36px;
  border-radius: 4px;
}

.toast-close-button {
  padding: 0;
  border-radius: 50%;
  background-color: var(--content-primary);
}

.async-toast__success {
  background: var(--bg-brand-mojito-secondary);
  color: var(--content-brand-mojito)
}

.async-toast__error {
  background: var(--bg-negative-secondary);
  color: var(--content-negative);
}

.async-toast__neutral {
  background: var(--bg-tertary);
  color: var(--content-secondary);
}
</style>
